import React, { useMemo, useEffect } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  Box,
  Text,
  useColorModeValue,
  HStack,
} from '@chakra-ui/react';
import { MatrixData } from '../../hooks/useFinancialMatrix';
// Re-export GroupByOption for use in parent
export type GroupByOption =
  | 'merchant'
  | 'merchant_year'
  | 'vendor'
  | 'vendor_year'
  | 'category'
  | 'category_year'
  | 'subcategory'
  | 'subcategory_year'
  | 'month'
  | 'year';


interface FinancialPivotProps {
  groupBy: GroupByOption;
  matrixData: MatrixData | null;
  onCellClick: (group: string | undefined, month: string | undefined, amount: number) => void;
  orientation?: 'vertical' | 'horizontal';
}

export const FinancialPivot: React.FC<FinancialPivotProps> = ({
  groupBy,
  matrixData,
  onCellClick,
  orientation = 'vertical',
}) => {
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const cellHoverBg = useColorModeValue('blue.50', 'blue.900');
  const tfootBg = useColorModeValue('gray.100', 'gray.900');
  const theadBg = useColorModeValue('gray.50', 'gray.800');

  const MONTH_NAMES = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  const formatPeriodLabel = (period: string): string => {
    const m = /^(\d{4})-(\d{2})$/.exec(period);
    if (!m) return period;
    const idx = parseInt(m[2], 10) - 1;
    return idx >= 0 && idx < 12 ? `${MONTH_NAMES[idx]}-${m[1]}` : period;
  };

  const formatSigned = (val: number): string => {
    const abs = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(Math.abs(val));
    return val > 0 ? `+${abs}` : val < 0 ? `-${abs}` : abs;
  };

  if (!matrixData) return null;

  const hasChecksH = matrixData.checkData?.totals.in > 0 || matrixData.checkData?.totals.out > 0;
  const isCatGroupBy = groupBy === 'category' || groupBy === 'category_year';

  const getGroupColLabel = (row: any): string => {
    if (isCatGroupBy) return row.subcategory ? `${row.category} / ${row.subcategory}` : row.category;
    return formatPeriodLabel(row.label);
  };

  const getDisplayMult = (row: any): number =>
    row.type === 'summary' && row.label === 'Income' ? 1 : -1;

  // ── Horizontal (transposed): rows = periods, columns = groups ─────────────
  if (orientation === 'horizontal') {
    return (
      <Box overflowX="auto" borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
        <Table variant="simple" size="sm">
          <Thead position="sticky" top={0} bg={theadBg} zIndex={1}>
            <Tr bg={theadBg}>
              <Th minW="120px">Period</Th>
              {matrixData.rows.map((row, i) => (
                <Th key={i} isNumeric minW="130px"
                  cursor="pointer"
                  onClick={() => onCellClick(isCatGroupBy ? `${row.category}::${row.subcategory}` : row.label, undefined, row.total)}>
                  {getGroupColLabel(row)}
                </Th>
              ))}
              {hasChecksH && matrixData.checkData.totals.in > 0 && <Th isNumeric minW="110px" color="green.600">Checks In</Th>}
              {hasChecksH && matrixData.checkData.totals.out > 0 && <Th isNumeric minW="110px" color="red.500">Checks Out</Th>}
              <Th isNumeric minW="110px" fontWeight="bold">Total</Th>
            </Tr>
          </Thead>
          <Tbody>
            {matrixData.columns.map(period => {
              const periodTotal = matrixData.colTotals[period];
              return (
                <Tr key={period} _hover={{ bg: hoverBg }}>
                  <Td fontWeight="medium" cursor="pointer"
                    _hover={{ textDecoration: 'underline' }}
                    onClick={() => onCellClick(undefined, period, periodTotal)}>
                    {formatPeriodLabel(period)}
                  </Td>
                  {matrixData.rows.map((row, i) => {
                    const dMult = getDisplayMult(row);
                    const val = row.periods[period] || 0;
                    const hasValue = val !== 0;
                    const drillGroup = isCatGroupBy
                      ? `${row.category}::${row.subcategory}`
                      : row.label;
                    return (
                      <Td key={i} isNumeric
                        color={val === 0 ? 'gray.300' : 'inherit'}
                        cursor={hasValue ? 'pointer' : 'default'}
                        _hover={hasValue ? { bg: cellHoverBg } : undefined}
                        onClick={() => hasValue && onCellClick(drillGroup, period, val)}>
                        {val !== 0 ? formatSigned(dMult * val) : '-'}
                      </Td>
                    );
                  })}
                  {hasChecksH && matrixData.checkData.totals.in > 0 && (() => {
                    const v = matrixData.checkData.periods[period]?.in || 0;
                    return <Td isNumeric color={v > 0 ? 'green.600' : 'gray.300'}>{v > 0 ? formatSigned(v) : '-'}</Td>;
                  })()}
                  {hasChecksH && matrixData.checkData.totals.out > 0 && (() => {
                    const v = matrixData.checkData.periods[period]?.out || 0;
                    return <Td isNumeric color={v > 0 ? 'red.500' : 'gray.300'}>{v > 0 ? formatSigned(-v) : '-'}</Td>;
                  })()}
                  <Td isNumeric fontWeight="bold"
                    color={periodTotal < 0 ? 'red.500' : periodTotal > 0 ? 'green.600' : 'inherit'}
                    cursor="pointer"
                    _hover={{ bg: cellHoverBg }}
                    onClick={() => onCellClick(undefined, period, periodTotal)}>
                    {formatSigned(periodTotal)}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
          <Tfoot>
            <Tr bg={tfootBg}>
              <Th fontWeight="black">Global Total</Th>
              {matrixData.rows.map((row, i) => {
                const dMult = getDisplayMult(row);
                const displayTotal = dMult * row.total;
                return (
                  <Th key={i} isNumeric fontWeight="black"
                    color={displayTotal < 0 ? 'red.500' : displayTotal > 0 ? 'green.600' : 'inherit'}
                    cursor="pointer"
                    onClick={() => onCellClick(isCatGroupBy ? `${row.category}::${row.subcategory}` : row.label, undefined, row.total)}>
                    {formatSigned(displayTotal)}
                  </Th>
                );
              })}
              {hasChecksH && matrixData.checkData.totals.in > 0 && (
                <Th isNumeric fontWeight="black" color="green.600">{formatSigned(matrixData.checkData.totals.in)}</Th>
              )}
              {hasChecksH && matrixData.checkData.totals.out > 0 && (
                <Th isNumeric fontWeight="black" color="red.500">{formatSigned(-matrixData.checkData.totals.out)}</Th>
              )}
              <Th isNumeric fontWeight="black" fontSize="md"
                color={matrixData.colTotals.grandTotal < 0 ? 'red.500' : matrixData.colTotals.grandTotal > 0 ? 'green.600' : 'inherit'}>
                {formatSigned(matrixData.colTotals.grandTotal)}
              </Th>
            </Tr>
          </Tfoot>
        </Table>
      </Box>
    );
  }

  // ── Vertical (default): rows = groups, columns = periods ──────────────────
  return (
    <Box overflowX="auto" borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
      <Table variant="simple" size="sm">
        <Thead position="sticky" top={0} bg={theadBg} zIndex={1}>
          <Tr bg={theadBg}>
            {groupBy === 'category' || groupBy === 'category_year' ? (
                <>
                    <Th minW="150px">Category</Th>
                    <Th minW="150px">Subcategory</Th>
                </>
            ) : groupBy === 'month' || groupBy === 'year' ? (
                <Th minW="200px">Summary</Th>
            ) : groupBy === 'vendor' || groupBy === 'vendor_year' ? (
                <Th minW="200px">Vendor</Th>
            ) : (
                <Th minW="200px">Merchant</Th>
            )}
            
            {matrixData.columns.map(period => (
                <Th key={period} isNumeric minW="100px">{formatPeriodLabel(period)}</Th>
            ))}
            <Th isNumeric minW="100px" fontWeight="bold">Total</Th>
          </Tr>
        </Thead>
        <Tbody>
            {matrixData.rows.map((row, idx) => {
                // In summary mode (month/year), the Income row stores amounts as positive and IS income → keep sign.
                // All other rows use Plaid convention (positive = expense) → negate for display.
                const displayMult = (row.type === 'summary' && row.label === 'Income') ? 1 : -1;
                return (
                <Tr key={idx} _hover={{ bg: hoverBg }}>
                    {groupBy === 'category' || groupBy === 'category_year' ? (
                        <>
                            <Td fontWeight="medium">{row.category}</Td>
                            <Td>{row.subcategory}</Td>
                        </>
                    ) : (
                        <Td fontWeight="medium">
                            <HStack spacing={2} cursor="pointer" onClick={() => onCellClick(row.label, undefined, row.total)}>
                               <Text _hover={{ textDecoration: 'underline', color: 'blue.500' }}>{formatPeriodLabel(row.label)}</Text>
                            </HStack>
                        </Td>
                    )}

                    {matrixData.columns.map(period => {
                        const val = row.periods[period] || 0;
                        const hasValue = val !== 0;
                        // Use composite key for category grouping to ensure correct filtering in drill-down
                        const drillDownGroup = (groupBy === 'category' || groupBy === 'category_year')
                            ? `${row.category}::${row.subcategory}`
                            : row.label;

                        return (
                            <Td
                                key={period}
                                isNumeric
                                color={val === 0 ? 'gray.300' : 'inherit'}
                                cursor={hasValue ? 'pointer' : 'default'}
                                _hover={hasValue ? { bg: cellHoverBg } : undefined}
                                onClick={() => hasValue && onCellClick(drillDownGroup, period, val)}
                            >
                                {val !== 0 ? formatSigned(displayMult * val) : '-'}
                            </Td>
                        );
                    })}
                    <Td
                        isNumeric
                        fontWeight="bold"
                        cursor="pointer"
                        _hover={{ bg: cellHoverBg, color: 'blue.500' }}
                        onClick={() => {
                             const drillDownGroup = (groupBy === 'category' || groupBy === 'category_year')
                                ? `${row.category}::${row.subcategory}`
                                : row.label;
                             onCellClick(drillDownGroup, undefined, row.total);
                        }}
                    >
                        {formatSigned(displayMult * row.total)}
                    </Td>
                </Tr>
                );
            })}
            {/* Check rows — only rendered when there is actual check data */}
            {(matrixData.checkData.totals.in > 0) && (
                <Tr _hover={{ bg: hoverBg }} borderTopWidth="2px">
                    {groupBy === 'category' || groupBy === 'category_year' ? (
                        <>
                            <Td fontWeight="semibold" color="green.600">Checks In</Td>
                            <Td color="green.600">Deposits</Td>
                        </>
                    ) : (
                        <Td fontWeight="semibold" color="green.600">Checks In</Td>
                    )}
                    {matrixData.columns.map(period => {
                        const val = matrixData.checkData.periods[period]?.in || 0;
                        return (
                            <Td key={period} isNumeric color={val > 0 ? 'green.600' : 'gray.300'}>
                                {val > 0 ? formatSigned(val) : '-'}
                            </Td>
                        );
                    })}
                    <Td isNumeric fontWeight="bold" color="green.600">
                        {formatSigned(matrixData.checkData.totals.in)}
                    </Td>
                </Tr>
            )}
            {(matrixData.checkData.totals.out > 0) && (
                <Tr _hover={{ bg: hoverBg }}>
                    {groupBy === 'category' || groupBy === 'category_year' ? (
                        <>
                            <Td fontWeight="semibold" color="red.500">Checks Out</Td>
                            <Td color="red.500">Payments</Td>
                        </>
                    ) : (
                        <Td fontWeight="semibold" color="red.500">Checks Out</Td>
                    )}
                    {matrixData.columns.map(period => {
                        const val = matrixData.checkData.periods[period]?.out || 0;
                        return (
                            <Td key={period} isNumeric color={val > 0 ? 'red.500' : 'gray.300'}>
                                {val > 0 ? formatSigned(-val) : '-'}
                            </Td>
                        );
                    })}
                    <Td isNumeric fontWeight="bold" color="red.500">
                        {formatSigned(-matrixData.checkData.totals.out)}
                    </Td>
                </Tr>
            )}
        </Tbody>
        <Tfoot>
            <Tr bg={tfootBg}>
                 <Th colSpan={(groupBy === 'category' || groupBy === 'category_year') ? 2 : 1} fontWeight="black">Global Total</Th>
                 {matrixData.columns.map(period => {
                     const val = matrixData.colTotals[period];
                     return (
                         <Th key={period} isNumeric fontWeight="black" color={val < 0 ? 'red.500' : val > 0 ? 'green.600' : 'inherit'}>
                             {formatSigned(val)}
                         </Th>
                     );
                 })}
                 <Th isNumeric fontWeight="black" fontSize="md" color={matrixData.colTotals.grandTotal < 0 ? 'red.500' : matrixData.colTotals.grandTotal > 0 ? 'green.600' : 'inherit'}>
                     {formatSigned(matrixData.colTotals.grandTotal)}
                 </Th>
            </Tr>
        </Tfoot>
      </Table>
    </Box>
  );
};
